import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const EMPTY_FORM = { ticker: '', quantity: '', accountLabel: '', isMutualFund: false, intradayProxy: '' };

function PortfolioPanel({ refreshKey }) {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => { fetchPortfolio(); }, [refreshKey]);

  // Auto-refresh prices every 5 minutes
  useEffect(() => {
    const timer = setInterval(fetchPortfolio, 300000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchPortfolio() {
    try {
      const res = await apiFetch('/api/portfolio');
      if (res.ok) setPortfolio(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const totals = useMemo(() => {
    let marketValue = 0, dayGain = 0;
    for (const h of portfolio) {
      marketValue += h.marketValue || 0;
      dayGain += h.dayGain || 0;
    }
    const dayGainPct = marketValue > 0 ? (dayGain / (marketValue - dayGain)) * 100 : 0;
    return { marketValue, dayGain, dayGainPct };
  }, [portfolio]);

  async function handleSave() {
    const payload = {
      ticker: form.ticker.trim(),
      quantity: parseFloat(form.quantity) || 0,
      costBasis: 0,
      accountLabel: form.accountLabel.trim(),
      isMutualFund: form.isMutualFund,
      intradayProxy: form.intradayProxy.trim() || null,
    };
    if (editingId) {
      const res = await apiFetch(`/api/portfolio/holdings/${editingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) fetchPortfolio();
    } else {
      const res = await apiFetch('/api/portfolio/holdings', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) fetchPortfolio();
    }
    resetForm();
  }

  async function handleDelete(id) {
    await apiFetch(`/api/portfolio/holdings/${id}`, { method: 'DELETE' });
    setPortfolio((prev) => prev.filter((h) => h.id !== id));
  }

  function startEdit(h) {
    setForm({
      ticker: h.ticker, quantity: String(h.quantity),
      accountLabel: h.accountLabel || '', isMutualFund: h.isMutualFund, intradayProxy: h.intradayProxy || '',
    });
    setEditingId(h.id);
    setShowForm(true);
  }

  function resetForm() { setForm({ ...EMPTY_FORM }); setShowForm(false); setEditingId(null); }

  function fmt(val) {
    return parseFloat(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  }
  function fmtPct(val) { return `${parseFloat(val) >= 0 ? '+' : ''}${parseFloat(val).toFixed(2)}%`; }

  if (loading) {
    return <section className="card"><h2>Portfolio</h2><p className="loading">Loading...</p></section>;
  }

  return (
    <section className="card pf-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Portfolio</h2>
        <button className="manage-btn" onClick={() => { resetForm(); setShowForm(true); }}>+ Add Holding</button>
      </div>

      {/* Portfolio Totals */}
      {portfolio.length > 0 && (
        <div className="wd-stats-row" style={{ marginBottom: 12 }}>
          <div className="wd-stat-box">
            <span className="wd-stat-num">{fmt(totals.marketValue)}</span>
            <span className="wd-stat-label">Market Value</span>
          </div>
          <div className="wd-stat-box">
            <span className="wd-stat-num" style={{ color: totals.dayGain >= 0 ? '#22c55e' : '#ef4444' }}>
              {totals.dayGain >= 0 ? '+' : ''}{fmt(totals.dayGain)} ({fmtPct(totals.dayGainPct)})
            </span>
            <span className="wd-stat-label">Day Change</span>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{ marginBottom: 12, padding: 12, background: '#16171f', borderRadius: 8, border: '1px solid #2a2b35' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="text" value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value })}
              placeholder="Ticker (e.g. AAPL)" className="sfin-tx-search" style={{ width: 100, flex: 'none' }} />
            <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              placeholder="Qty" style={{ width: 80, padding: '6px 8px', background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, fontSize: 13 }} />
            <input type="text" value={form.accountLabel} onChange={(e) => setForm({ ...form, accountLabel: e.target.value })}
              placeholder="Account label" className="sfin-tx-search" style={{ width: 120, flex: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <label className="settings-toggle-label" style={{ fontSize: 13, gap: 6 }}>
              <input type="checkbox" checked={form.isMutualFund} onChange={(e) => setForm({ ...form, isMutualFund: e.target.checked })} />
              <span>Mutual fund</span>
            </label>
            {form.isMutualFund && (
              <input type="text" value={form.intradayProxy} onChange={(e) => setForm({ ...form, intradayProxy: e.target.value })}
                placeholder="Intraday ETF proxy (e.g. OAKM)" className="sfin-tx-search" style={{ width: 160, flex: 'none' }} />
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="manage-btn" onClick={handleSave} disabled={!form.ticker || !form.quantity}>
                {editingId ? 'Update' : 'Add'}
              </button>
              <button className="manage-btn" onClick={resetForm} style={{ background: '#2a2b35' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Holdings Table */}
      {portfolio.length === 0 && !showForm && (
        <p style={{ color: '#71717a', fontSize: 13 }}>No holdings configured. Add your stock and mutual fund positions.</p>
      )}

      {portfolio.length > 0 && (
        <div className="pf-holdings">
          <div className="pf-header">
            <span>Ticker</span><span>Price</span><span>Day Chg</span><span>Qty</span><span>Mkt Value</span><span>Day Gain</span><span></span>
          </div>
          {portfolio.map((h) => {
            const dayUp = h.dayChange >= 0;
            return (
              <div key={h.id} className="pf-row">
                <span className="pf-ticker">
                  {h.ticker}
                  {h.usingProxy && <span className="pf-proxy-badge" title={`Using ${h.proxyTicker} for intraday`}>~{h.proxyTicker}</span>}
                  {h.accountLabel && <span className="pf-account-label">{h.accountLabel}</span>}
                </span>
                <span>{fmt(h.currentPrice)}</span>
                <span className={dayUp ? 'positive' : 'negative'}>
                  {fmtPct(h.dayChangePct)}
                </span>
                <span>{h.quantity}</span>
                <span>{fmt(h.marketValue)}</span>
                <span className={dayUp ? 'positive' : 'negative'}>
                  {dayUp ? '+' : ''}{fmt(h.dayGain)}
                </span>
                <span className="pf-actions">
                  <button className="wg-action-btn wg-edit-btn" onClick={() => startEdit(h)} title="Edit">&#x270E;</button>
                  <button className="wg-action-btn wg-delete-btn" onClick={() => handleDelete(h.id)} title="Delete">&times;</button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default PortfolioPanel;
